import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function ProtectedRoute({ children, requireRole }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, loading } = useAuth();

  useEffect(() => {
    // If user already in context (from AuthCallback or previous load)
    if (user) {
      setIsAuthenticated(true);
      return;
    }

    // If user data passed from AuthCallback via navigation state
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    // If still loading auth context, wait
    if (loading) {
      return;
    }

    const checkAuth = async () => {
      try {
        // Get session token from localStorage as fallback
        const sessionToken = localStorage.getItem('session_token');

        const headers = {};
        if (sessionToken) {
          headers['Authorization'] = `Bearer ${sessionToken}`;
        }

        const response = await fetch(
          `/api/auth/me`,
          {
            credentials: 'include',
            headers
          }
        );

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setIsAuthenticated(true);
          return;
        }

        // 🛡️ Kiosk-friendly: only redirect on 401 (explicit invalid session).
        // Any other status = transient backend outage. Keep the token, stay
        // on the page, and let the retry logic reconnect when the pod is up.
        if (response.status === 401) {
          console.log('Session invalid (401) — redirecting to login');
          localStorage.removeItem('session_token');
          setIsAuthenticated(false);
          navigate('/login');
          return;
        }

        // 5xx / 520 / timeout — assume we were authenticated previously; wait it out.
        console.warn(`Auth check got ${response.status} — keeping session, will retry.`);
        if (sessionToken) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          navigate('/login');
        }
      } catch (error) {
        // Network error — DO NOT clear the token or redirect. TV kiosk should
        // survive brief pod restarts / cold-boot 520s without kicking to login.
        console.warn('Auth check network error — keeping session:', error.message);
        const sessionToken = localStorage.getItem('session_token');
        if (sessionToken) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          navigate('/login');
        }
      }
    };

    checkAuth();
  }, [navigate, location.state, user, setUser, loading]);

  // TV kiosk role → force to /tv, they can't access anything else
  useEffect(() => {
    if (isAuthenticated && user?.role === 'tv_kiosk' && location.pathname !== '/tv') {
      navigate('/tv', { replace: true });
    }
  }, [isAuthenticated, user, location.pathname, navigate]);

  // Loading state
  if (isAuthenticated === null || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-2 border-profit border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - will redirect via useEffect
  if (!isAuthenticated) {
    return null;
  }

  // Role gate (e.g. admin-only routes)
  if (requireRole && user?.role && user.role !== requireRole) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <div className="max-w-md text-center space-y-4">
          <div className="text-6xl">🚫</div>
          <h1 className="text-2xl font-bold">Access Restricted</h1>
          <p className="text-muted-foreground">
            This section is for admin users only. You're logged in with the TV Kiosk PIN.
          </p>
        </div>
      </div>
    );
  }

  // Authenticated - render children
  return children;
}
